import { useNavigate } from "@tanstack/react-router";
import { Bell, Package, PanelLeft, PanelLeftClose } from "lucide-react";
import type { JSX } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { BellDrawer, useListNotifications } from "@/features/notifications";
import { useAuth, useCan } from "@/shared/auth";
import { LanguageToggle } from "@/shared/components/LanguageToggle";
import { UserMenu } from "@/shared/components/UserMenu";

export function TopBar({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}): JSX.Element {
  const { t } = useTranslation(["auth", "common", "nav"]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Only surface notifications to users who hold the read permission — keeps the
  // bell (and its query) hidden for roles without access, so they never hit a 403.
  const canSeeNotifications = useCan("notifications:read");
  const { data } = useListNotifications(undefined, { enabled: canSeeNotifications });
  const unreadCount = data?.unread_count ?? 0;

  function handleLogout(): void {
    logout();
    void navigate({ to: "/login" });
  }

  const initials = (user?.display_name || user?.email || "")
    .replace(/[^A-Za-z]/g, "")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header
      className="sticky top-0 z-40 flex h-[60px] items-center justify-between border-b border-[var(--premium-border)] bg-white/85 px-6 shadow-[var(--sh-xs)] backdrop-blur-[12px] backdrop-saturate-[180%]"
      data-testid="topbar"
    >
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          aria-label={collapsed ? t("nav:action.expand") : t("nav:action.collapse")}
          aria-expanded={!collapsed}
          aria-controls="sidebar-nav"
          data-testid="topbar-nav-toggle"
          className="flex h-8 w-8 items-center justify-center rounded-lg text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
          onClick={onToggle}
        >
          {collapsed ? (
            <PanelLeft className="h-[18px] w-[18px]" aria-hidden />
          ) : (
            <PanelLeftClose className="h-[18px] w-[18px]" aria-hidden />
          )}
        </button>
        <span
          className="grid h-7 w-7 place-items-center rounded-lg text-white"
          style={{
            background: "linear-gradient(140deg,#3b82f6,var(--brand-500) 45%,var(--brand-700))",
            boxShadow: "var(--sh-xs)",
          }}
        >
          <Package className="h-4 w-4" aria-hidden />
        </span>
        <span
          className="text-[16px] font-extrabold tracking-tight text-[var(--text-primary)]"
          data-testid="topbar-logo"
        >
          {t("common:app.name")}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <LanguageToggle />
        {canSeeNotifications && (
          <>
            <button
              type="button"
              aria-label={t("nav:item.notifications")}
              data-testid="topbar-bell"
              className="relative flex h-8 w-8 items-center justify-center rounded-lg text-[var(--text-secondary)] transition-colors hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
              onClick={() => setDrawerOpen(true)}
            >
              <Bell className="h-[18px] w-[18px]" aria-hidden />
              {unreadCount > 0 && (
                <span
                  data-testid="topbar-bell-badge"
                  className="absolute right-0.5 top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[var(--brand-500)] px-1 text-[9px] font-bold leading-none text-white"
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                  <span className="sr-only">{t("nav:item.notifications_unread")}</span>
                </span>
              )}
            </button>
            <BellDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
          </>
        )}
        {user && (
          <UserMenu
            email={user.email}
            initials={initials || "U"}
            logoutLabel={t("auth:action.logout")}
            onLogout={handleLogout}
          />
        )}
      </div>
    </header>
  );
}
